import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Activity, Phone, AlertCircle, Bell, Monitor } from 'lucide-react';
import { toast } from 'sonner';

const activityIcons = {
  device_online: Monitor,
  device_offline: Monitor,
  call_started: Phone,
  call_ended: Phone,
  emergency_trigger: AlertCircle,
  reminder_trigger: Bell,
  family_user_registered: Activity
};

const activityColors = {
  device_online: 'text-green-600',
  device_offline: 'text-gray-600',
  call_started: 'text-blue-600',
  call_ended: 'text-gray-600',
  emergency_trigger: 'text-red-600',
  reminder_trigger: 'text-yellow-600',
  family_user_registered: 'text-indigo-600'
};

export default function ActivityFeed() {
  const { householdId } = useParams();
  const navigate = useNavigate();
  const [household, setHousehold] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [householdId]);

  const fetchData = async () => {
    try {
      const [householdRes, activitiesRes] = await Promise.all([
        axios.get(`${API}/households/${householdId}`),
        axios.get(`${API}/activity/${householdId}?limit=50`)
      ]);
      
      setHousehold(householdRes.data);
      setActivities(activitiesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden der Aktivitäten');
    } finally {
      setLoading(false);
    }
  };

  const formatActivityText = (activity) => {
    switch (activity.type) {
      case 'device_online':
        return `Gerät ${activity.payload.serial_number || activity.payload.device_id} ist online`;
      case 'device_offline':
        return `Gerät ${activity.payload.serial_number || activity.payload.device_id} ist offline`;
      case 'call_started':
        return `Videoanruf gestartet (${activity.payload.type})`;
      case 'call_ended':
        return `Videoanruf beendet`;
      case 'emergency_trigger':
        return 'Notfall ausgelöst!';
      case 'reminder_trigger':
        return 'Erinnerung ausgelöst';
      case 'family_user_registered':
        return `${activity.payload.name} wurde als ${activity.payload.role === 'admin' ? 'Administrator' : 'Mitglied'} hinzugefügt`;
      default:
        return activity.type;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="outline"
          onClick={() => navigate(`/households/${householdId}`)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-6 h-6" />
              Aktivitäten - {household?.name}
            </CardTitle>
            <CardDescription>
              Letzte 50 Ereignisse in diesem Haushalt (aktualisiert alle 10 Sekunden)
            </CardDescription>
          </CardHeader>
        </Card>

        <div className="space-y-3">
          {activities.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center">
                <Activity className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">Noch keine Aktivitäten</p>
              </CardContent>
            </Card>
          ) : (
            activities.map((activity) => {
              const Icon = activityIcons[activity.type] || Activity;
              const color = activityColors[activity.type] || 'text-gray-600';
              
              return (
                <Card key={activity.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-full bg-gray-100 ${color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{formatActivityText(activity)}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          {new Date(activity.created_at).toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </p>
                        {activity.payload && Object.keys(activity.payload).length > 0 && (
                          <details className="mt-2">
                            <summary className="text-xs text-gray-500 cursor-pointer">Details anzeigen</summary>
                            <pre className="text-xs mt-2 p-2 bg-gray-50 rounded overflow-auto">
                              {JSON.stringify(activity.payload, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                      <Badge variant={activity.type.includes('emergency') ? 'destructive' : 'secondary'}>
                        {activity.type.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
